/**
 * Spec 01 §16's sentence, fixed and unhideable.
 *
 * *"Không dùng làm căn cứ kỷ luật duy nhất"*, and Task 57's acceptance criterion says
 * it may not be hidden. So there is deliberately no close control, no collapse
 * control, no `hidden`, no `aria-expanded` and no dismissal in `localStorage` — and
 * that absence is asserted, because "we did not add one" is a habit and "there is not
 * one" is a property.
 *
 * It is a **quiet** note rather than a high-severity panel. Nothing is wrong when it
 * is on screen — it is on screen always — and painting a standing legal sentence in
 * warning amber spends the one channel this page needs for a refusal that really is
 * one (DESIGN.md §4: colour means something). Quiet is not hidden: it keeps its own
 * ruled block, its label and its glyph, and none of the three is dismissible.
 *
 * It is rendered **first**, above every AI result on the page, rather than as a
 * footer. Task 44 already made the argument while the slot was empty: "a disclaimer
 * that appears only once the panel has content is a disclaimer that is missing on the
 * first day it matters". The same logic puts it above rather than below — a
 * disclaimer under eight hundred pixels of model prose is one that is read after the
 * decision.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { Icon } from '../../states/icons';

export function AiDisclaimer({
  testId = 'm4-disclaimer',
}: {
  readonly testId?: string;
}): ReactNode {
  const { t } = useI18n();
  return (
    <aside className="wp-ai-note" data-testid={testId}>
      <Icon name="info" className="wp-ai-note__icon" />
      <div>
        {/* The lead clause, in sentence case. It wore `.wp-label` — 11px caps — and the
            sentence is not a label: set in capitals it ran to two lines of shouting
            Vietnamese with the diacritics colliding into the line above, which is the
            exact failure Luật Nhãn In Hoa names (DESIGN.md §6: uppercase belongs to
            11px keys and nowhere else). Weight carries it now, on the lead clause and
            on nothing else in the note. */}
        <p className="wp-ai-note__lead">{t('m4.disclaimer.title')}</p>
        <p data-testid={`${testId}-body`}>{t('m4.disclaimer.body')}</p>
        <p className="wp-small wp-muted" data-testid={`${testId}-fixed`}>
          {t('m4.disclaimer.fixed')}
        </p>
      </div>
    </aside>
  );
}
